import { FastshiftExpectedError } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Breadcrumbs from '@atlaskit/breadcrumbs/breadcrumbs';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs/breadcrumbs-item';
import { BreadcrumbsSkeleton } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton';
import { BreadcrumbsSkeletonItem } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton-item';
import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';

import { FourOhFourDisplay } from '@/common/displays';
import { useRoutes } from '@/common/hooks';
import { getOrganizationDisplayName } from '@/common/organizations-flyout/src/utils';
import { useFastshiftSelector } from '@/state';

import { useOrganizationDetailsPageAnalytics } from './hooks/use-organization-details-page-analytics';
import { messages } from './messages';
import { OrganizationDcInstancesTable } from './organization-dc-instances-table/organization-dc-instances-table';
import { OrganizationSummary } from './summary/organization-summary';

interface OrganizationDetailsBreadcrumbsProps {
	isLoading: boolean;
	organizationName: string;
	organizationHref: string;
	organizationsLabel: string;
	organizationsHref: string;
}

// TODO: move to a common component when we have the dc details page
const OrganizationDetailsBreadcrumbs = ({
	isLoading,
	organizationName,
	organizationHref,
	organizationsLabel,
	organizationsHref,
}: OrganizationDetailsBreadcrumbsProps) =>
	isLoading ? (
		<BreadcrumbsSkeleton testId="organization-breadcrumb-skeleton">
			<BreadcrumbsSkeletonItem width={100} />
			<BreadcrumbsSkeletonItem width={100} />
		</BreadcrumbsSkeleton>
	) : (
		<Breadcrumbs>
			<BreadcrumbsItem href={organizationsHref} text={organizationsLabel} />
			<BreadcrumbsItem href={organizationHref} text={organizationName} />
		</Breadcrumbs>
	);

export const FastshiftOrganizationDetailsPage = () => {
	useOrganizationDetailsPageAnalytics();
	const { formatMessage } = useIntl();
	const { paths } = useRoutes();

	const { domainId, organizationId } = useParams<{
		domainId: string;
		organizationId: string;
	}>();
	const organizationDetail = useFastshiftSelector((state) => state.organizationDetail);

	if (domainId === undefined || organizationId === undefined) {
		return <FourOhFourDisplay />;
	}

	const hasMatchingOrganizationDetail =
		organizationDetail.domainId === domainId && organizationDetail.orgId === organizationId;
	const organizationSummary = hasMatchingOrganizationDetail
		? organizationDetail.summary.data
		: null;
	const isOrganizationNameLoading =
		!hasMatchingOrganizationDetail || organizationDetail.summary.pending;
	const organizationName = getOrganizationDisplayName(
		{
			orgId: organizationId,
			orgName: organizationSummary?.orgName ?? null,
		},
		{ formatMessage },
	);
	const isNotFound =
		hasMatchingOrganizationDetail &&
		(organizationDetail.summary.error === FastshiftExpectedError.NOT_FOUND ||
			organizationDetail.dcInstances.error === FastshiftExpectedError.NOT_FOUND);

	if (isNotFound) {
		return <FourOhFourDisplay />;
	}

	return (
		<Stack space="space.300">
			<OrganizationDetailsBreadcrumbs
				isLoading={isOrganizationNameLoading}
				organizationName={organizationName}
				organizationHref={paths.domainScopedOrganizationDetails ?? ''}
				organizationsLabel={formatMessage(messages.organizationsListBreadcrumb)}
				organizationsHref={paths.domainScopedOrganizations ?? ''}
			/>
			<Stack space="space.150">
				{isOrganizationNameLoading ? (
					<Skeleton width="300px" height="32px" testId="organization-name-skeleton" />
				) : (
					<Heading size="large" as="h1">
						{organizationName}
					</Heading>
				)}
			</Stack>
			<OrganizationSummary domainId={domainId} orgId={organizationId} />
			<OrganizationDcInstancesTable domainId={domainId} orgId={organizationId} />
		</Stack>
	);
};
